import { useState, useRef } from "react";
import { type Lead, type LeadCategory, type LeadStatus, type LeadConversionMetrics } from "../../lib/lead-types.ts";
import {
  updateLeadStatus,
  deleteLead,
  downloadLeadsCSV,
  importLeadsFromCSV,
} from "../../lib/lead-storage.ts";
import { LeadConversionFunnel } from "./LeadConversionFunnel.tsx";
import { toast } from "../Toast.tsx";

interface Props {
  leads: Lead[];
  metrics: LeadConversionMetrics;
  onRefresh: () => void;
  onSelectLeadForOutreach: (leadId: string) => void;
  onOpenAddModal: () => void;
  onOpenEditModal: (lead: Lead) => void;
}

const CATEGORY_LABELS: Record<LeadCategory, string> = {
  expense_management: "Expense Mgmt",
  saas_pricing: "SaaS Pricing",
  invoicing_payroll: "Invoicing & Payroll",
  ecommerce: "E-Commerce",
  fintech: "Fintech",
  other: "Other",
};

const STATUS_LABELS: Record<LeadStatus, { label: string; cls: string }> = {
  new: { label: "New", cls: "badge-neutral" },
  contacted: { label: "Contacted", cls: "badge-accent" },
  follow_up_needed: { label: "Follow-up Needed", cls: "badge-warning" },
  replied: { label: "Replied", cls: "badge-info" },
  converted: { label: "Converted", cls: "badge-ok" },
  not_interested: { label: "Not Interested", cls: "badge-danger" },
};

export function LeadDirectory({
  leads,
  metrics,
  onRefresh,
  onSelectLeadForOutreach,
  onOpenAddModal,
  onOpenEditModal,
}: Props) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importCsvText, setImportCsvText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter leads
  const filteredLeads = leads.filter((lead) => {
    if (selectedCategory !== "all" && lead.category !== selectedCategory) {
      return false;
    }
    if (selectedStatus !== "all" && lead.status !== selectedStatus) {
      return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchCompany = lead.company?.toLowerCase().includes(q);
      const matchEmail = lead.email?.toLowerCase().includes(q);
      const matchName = lead.contactName?.toLowerCase().includes(q);
      const matchWebsite = lead.website?.toLowerCase().includes(q);
      const matchNotes = lead.notes?.toLowerCase().includes(q);
      const matchUseCase = lead.useCase?.toLowerCase().includes(q);
      if (!matchCompany && !matchEmail && !matchName && !matchWebsite && !matchNotes && !matchUseCase) {
        return false;
      }
    }
    return true;
  });

  function handleStatusChange(leadId: string, newStatus: LeadStatus) {
    updateLeadStatus(leadId, newStatus);
    toast(`Lead marked as ${STATUS_LABELS[newStatus].label}.`);
    onRefresh();
  }

  function handleDelete(leadId: string, company: string) {
    if (window.confirm(`Are you sure you want to delete ${company} from your leads directory?`)) {
      deleteLead(leadId);
      toast(`Deleted ${company}.`);
      onRefresh();
    }
  }

  function handleCsvFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setImportCsvText(content);
        setImportModalOpen(true);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleExecuteImport() {
    if (!importCsvText.trim()) {
      toast("CSV content is empty.");
      return;
    }
    const result = importLeadsFromCSV(importCsvText);
    if (result.errors.length > 0) {
      toast(`Imported ${result.importedCount} leads with ${result.errors.length} errors.`);
    } else {
      toast(`Successfully imported ${result.importedCount} prospective leads!`);
    }
    setImportCsvText("");
    setImportModalOpen(false);
    onRefresh();
  }

  return (
    <div className="lead-directory-space space-y-4">
      {/* 1. App Conversion Funnel Strip */}
      <LeadConversionFunnel metrics={metrics} />

      {/* 2. Directory Toolbar */}
      <div className="lead-toolbar card">
        <div className="lead-toolbar-search">
          <div className="search-input-wrap">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="search-icon"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="input input-search"
              placeholder="Search by company, website, contact, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <select
            className="select select-sm"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Industries</option>
            <option value="expense_management">Expense Management</option>
            <option value="saas_pricing">SaaS Pricing / PPP</option>
            <option value="invoicing_payroll">Invoicing &amp; Payroll</option>
            <option value="ecommerce">E-Commerce</option>
            <option value="fintech">Fintech</option>
            <option value="other">Other</option>
          </select>

          <select
            className="select select-sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="new">New (Uncontacted)</option>
            <option value="contacted">Contacted</option>
            <option value="follow_up_needed">Follow-up Needed</option>
            <option value="replied">Replied</option>
            <option value="converted">Converted (App Users)</option>
            <option value="not_interested">Not Interested</option>
          </select>
        </div>

        <div className="lead-toolbar-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv,text/csv"
            onChange={handleCsvFileSelect}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => fileInputRef.current?.click()}
            title="Import leads from spreadsheet"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Import CSV
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={downloadLeadsCSV}
            title="Download full lead list as CSV"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Export CSV
          </button>

          <button type="button" className="btn btn-primary btn-sm" onClick={onOpenAddModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Lead
          </button>
        </div>
      </div>

      {/* 3. Leads Table */}
      <div className="card table-card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Company &amp; Website</th>
                <th>Contact Person</th>
                <th>Category</th>
                <th>Status</th>
                <th>Outreach &amp; Activity</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 muted">
                    {search || selectedCategory !== "all" || selectedStatus !== "all"
                      ? "No prospective leads match the selected filters."
                      : "No prospective leads yet. Click 'Add Lead' to get started."}
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => {
                  const statusInfo = STATUS_LABELS[lead.status] || STATUS_LABELS.new;
                  const isContacted = (lead.followUpCount ?? 0) > 0;

                  return (
                    <tr key={lead.id} className="lead-row">
                      {/* Company & Website */}
                      <td>
                        <div className="lead-company-cell">
                          <span className="font-semibold text-white">{lead.company}</span>
                          {lead.website ? (
                            <a
                              href={lead.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="lead-website-link text-xs mono"
                              title={`Open ${lead.website}`}
                            >
                              {lead.website.replace(/^https?:\/\//i, "").replace(/\/$/, "")} ↗
                            </a>
                          ) : (
                            <span className="muted text-xs">No website</span>
                          )}
                          {lead.notes && (
                            <div className="lead-notes-preview text-xs muted truncate" title={lead.notes}>
                              {lead.notes}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Contact Person & Email */}
                      <td>
                        <div className="lead-contact-cell">
                          <span className="text-sm font-medium">
                            {lead.contactName || "-"}
                          </span>
                          <a
                            href={`mailto:${lead.email}`}
                            className="lead-email-link text-xs mono text-accent"
                          >
                            {lead.email}
                          </a>
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="badge badge-neutral text-xs">
                          {CATEGORY_LABELS[lead.category] || lead.category}
                        </span>
                      </td>

                      {/* Status & Quick Transition */}
                      <td>
                        <div className="status-dropdown-wrap">
                          <select
                            className={`badge ${statusInfo.cls} status-select`}
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value as LeadStatus)}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="follow_up_needed">Follow-up Needed</option>
                            <option value="replied">Replied</option>
                            <option value="converted">Converted</option>
                            <option value="not_interested">Not Interested</option>
                          </select>
                        </div>
                      </td>

                      {/* Outreach & Activity */}
                      <td>
                        <div className="lead-activity-cell text-xs">
                          {isContacted ? (
                            <>
                              <span className="font-medium text-accent">
                                {lead.followUpCount === 1
                                  ? "1 email sent"
                                  : `${lead.followUpCount} emails sent`}
                              </span>
                              <span className="muted">
                                {lead.lastContactedAt
                                  ? `Last: ${new Date(lead.lastContactedAt).toLocaleDateString()}`
                                  : ""}
                              </span>
                            </>
                          ) : (
                            <span className="muted">Not contacted yet</span>
                          )}
                          {lead.status === "converted" && lead.convertedAt && (
                            <span className="text-ok font-medium">
                              Converted: {new Date(lead.convertedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="text-right">
                        <div className="lead-actions-inline">
                          {/* 1-by-1 Outreach or Follow-up button */}
                          <button
                            type="button"
                            className={`btn btn-xs ${isContacted ? "btn-secondary" : "btn-primary"}`}
                            onClick={() => onSelectLeadForOutreach(lead.id)}
                            title={isContacted ? "Send follow-up note" : "Send initial outreach email"}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M22 2L11 13" />
                              <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                            {isContacted ? "Follow-up" : "Email"}
                          </button>

                          {/* Quick Convert button */}
                          {lead.status !== "converted" && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-ok"
                              onClick={() => handleStatusChange(lead.id, "converted")}
                              title="Mark as converted app user"
                            >
                              ✓ Convert
                            </button>
                          )}

                          {/* Edit button */}
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-xs"
                            onClick={() => onOpenEditModal(lead)}
                            aria-label="Edit lead"
                            title="Edit lead"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>

                          {/* Delete button */}
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-xs text-danger"
                            onClick={() => handleDelete(lead.id, lead.company)}
                            aria-label="Delete lead"
                            title="Delete lead"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV Import Modal */}
      {importModalOpen && (
        <div className="modal-backdrop" onClick={() => setImportModalOpen(false)}>
          <div
            className="modal-box modal-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-head">
              <h3>Import Leads from CSV</h3>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={() => setImportModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body space-y-3">
              <p className="text-xs muted">
                Paste your CSV content below or review uploaded file. Columns should ideally include
                headers such as <code>Company</code>, <code>Email</code>, <code>Website</code>,{" "}
                <code>Contact Name</code>, <code>Use Case</code>, <code>Pain Point</code>.
              </p>
              <textarea
                className="textarea w-full font-mono text-xs"
                rows={10}
                value={importCsvText}
                onChange={(e) => setImportCsvText(e.target.value)}
                placeholder="Company,Email,Website,Contact Name..."
              />
            </div>
            <div className="modal-foot">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setImportModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleExecuteImport}
              >
                Import Leads
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
